import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, BookOpenCheck, Building2, Check, GraduationCap, MonitorPlay, School, University } from 'lucide-react';
import { AuthPageFrame } from '../../components/auth/AuthPageFrame';
import { useAuth } from '../../hooks/useAuth';
import { useTenant } from '../../hooks/useTenant';
import { isTenantSlugAvailable } from '../../services/tenantService';
import { createSlug, isValidTenantSlug } from '../../utils/slug';
import { friendlyFirebaseError } from '../../utils/validators';
import { sendSetupCompleteEmail } from '../../services/communicationService';

const tenantTypes = [
  { label: 'Private tutor', description: 'Teach online, in person, or both', value: 'private_instructor', icon: GraduationCap },
  { label: 'School', description: 'For primary or secondary', value: 'school', icon: School },
  { label: 'University', description: 'For higher education', value: 'university', icon: University },
  { label: 'College', description: 'For career programmes', value: 'college', icon: BookOpenCheck },
  { label: 'Learning centre', description: 'For training providers', value: 'training_center', icon: Building2 },
  { label: 'Virtual academy', description: 'Teach entirely online', value: 'virtual_academy', icon: MonitorPlay },
];
const authShowcaseVideo = 'https://res.cloudinary.com/ubunifu/video/upload/v1607186910/pexels-rodnae-productions-5923291_jd1rwl.mp4';

export function SetupPage() {
  const { user, profile } = useAuth();
  const { createTenant } = useTenant();
  const navigate = useNavigate();
  const suggestedName = useMemo(() => `${user?.displayName?.split(' ')[0] || 'My'}'s Academy`, [user]);
  const [values, setValues] = useState({ type: 'private_instructor', name: suggestedName, slug: createSlug(suggestedName), timezone: profile?.timezone || 'Africa/Nairobi', currency: profile?.currency || 'KES' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [slugCheck, setSlugCheck] = useState({ slug: '', status: 'idle' });

  useEffect(() => {
    const slug = values.slug;
    if (!slug) {
      setSlugCheck({ slug, status: 'idle' });
      return undefined;
    }
    if (!isValidTenantSlug(slug)) {
      setSlugCheck({ slug, status: 'invalid' });
      return undefined;
    }

    let cancelled = false;
    setSlugCheck({ slug, status: 'checking' });
    const timeoutId = window.setTimeout(async () => {
      try {
        const available = await isTenantSlugAvailable(slug);
        if (!cancelled) setSlugCheck({ slug, status: available ? 'available' : 'taken' });
      } catch (checkError) {
        console.error(checkError);
        if (!cancelled) setSlugCheck({ slug, status: 'error' });
      }
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [values.slug]);

  function updateName(name) {
    setValues((current) => ({ ...current, name, slug: createSlug(name) }));
  }

  async function onSubmit(event) {
    event.preventDefault();
    setError('');
    if (!values.name.trim()) {
      setError('Enter an organization name.');
      return;
    }
    if (!isValidTenantSlug(values.slug)) {
      setError('Use a safe academy URL with lowercase letters, numbers, and hyphens.');
      return;
    }
    if (slugCheck.slug !== values.slug || slugCheck.status !== 'available') {
      setError(slugCheck.status === 'taken' ? 'This academy URL is already taken. Choose another.' : 'Wait for the academy URL availability check to finish.');
      return;
    }
    setLoading(true);
    try {
      const tenant = await createTenant(values);
      try {
        await sendSetupCompleteEmail({ user, profile, tenant });
      } catch (communicationError) {
        console.error('Setup completion email could not be sent', communicationError);
      }
      navigate('/dashboard', { replace: true });
    } catch (err) {
      console.error(err);
      setError(err.code === 'slug-unavailable' ? err.message : friendlyFirebaseError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthPageFrame
      className="smart-setup-page"
      eyebrow="Create your workspace"
      title="What are you setting up?"
      description="Choose the learning space you are building, then give it a name. You can refine everything else later."
      step="Step 2 of 2"
      showcaseVideo={authShowcaseVideo}
    >
      <form className="smart-setup-form" onSubmit={onSubmit}>
        {error ? <div className="form-error">{error}</div> : null}
        <label className="smart-setup-field" htmlFor="tenant-name">
          <span>Name your workspace</span>
          <input id="tenant-name" autoComplete="organization" placeholder="e.g. Bright Future Academy" required value={values.name} onChange={(event) => updateName(event.target.value)} />
        </label>
        <fieldset className="smart-setup-types">
          <legend>Choose your learning space</legend>
          <div className="smart-setup-type-grid">
            {tenantTypes.map((type) => {
              const Icon = type.icon;
              const selected = values.type === type.value;
              return (
                <button
                  type="button"
                  aria-pressed={selected}
                  className={selected ? 'smart-setup-type-option is-selected' : 'smart-setup-type-option'}
                  key={type.value}
                  onClick={() => setValues((current) => ({ ...current, type: type.value }))}
                >
                  <span className="smart-setup-type-icon"><Icon size={17} aria-hidden="true" /></span>
                  <span className="smart-setup-type-copy"><strong>{type.label}</strong><small>{type.description}</small></span>
                  {selected ? <span className="smart-setup-type-check"><Check size={13} strokeWidth={3} aria-hidden="true" /></span> : null}
                </button>
              );
            })}
          </div>
        </fieldset>

        <div className="smart-setup-details">
          <label className="smart-setup-field" htmlFor="tenant-slug">
            <span>Your SmartCampus address (SmartCampus subdomain)</span>
            <div className="smart-setup-address">
              <input id="tenant-slug" required aria-describedby="tenant-url-hint tenant-slug-status" value={values.slug} onChange={(event) => { setError(''); setValues((current) => ({ ...current, slug: createSlug(event.target.value) })); }} />
              <span>.smartcampus.africa</span>
            </div>
          </label>
        </div>
        <div className="smart-setup-live-url" aria-live="polite"><strong>https://{values.slug || 'your-subdomain'}.smartcampus.africa</strong></div>
        <p className={`smart-setup-slug-status is-${slugCheck.status}`} id="tenant-slug-status" aria-live="polite">
          {slugCheck.status === 'checking' ? 'Checking availability…' : null}
          {slugCheck.status === 'available' ? 'This address is available.' : null}
          {slugCheck.status === 'taken' ? 'This address is already taken. Choose another.' : null}
          {slugCheck.status === 'invalid' ? 'Use at least 2 characters with lowercase letters, numbers, or hyphens.' : null}
          {slugCheck.status === 'error' ? 'We could not check this address. Try again.' : null}
        </p>
        <p className="smart-setup-url-hint" id="tenant-url-hint">Share this link with learners to sign up for courses, log in for classes, or access their student account. Your staff will use it to access their workspace too. You can also use it in ads and campaigns to drive more course sign-ups.</p>
        {loading ? <p className="smart-setup-loading">Creating your workspace…</p> : null}
        <button className="smart-auth-submit smart-setup-submit" type="submit" disabled={loading || slugCheck.slug !== values.slug || slugCheck.status !== 'available'}>{loading ? 'Creating your workspace…' : <>Create my workspace <ArrowRight size={19} /></>}</button>
      </form>
    </AuthPageFrame>
  );
}
